export const pilatesContent = {
  hero: {
    description: "Eight to ten reformer beds. Controlled capacity. Expert instruction focused on alignment, breath, and sustainable strength. This is movement as medicine—precise, intentional, and designed for longevity."
  },
  philosophy: {
    intro: "RE:SET Pilates is not about intensity for intensity's sake. We prioritize form over speed, depth over volume, and sustainable strength over short-term gains.",
    detail: "Our boutique studio is intentionally small—eight to ten reformer beds maximum—to ensure every participant receives individual attention and expert guidance. This is movement practice built for the long game.",
    principles: [
      "Alignment-first approach to prevent injury and build sustainable strength",
      "Breath work integrated into every movement for nervous system regulation",
      "Small class sizes (maximum 10 participants) for personalized instruction",
      "Consistent instructor relationships for progressive development",
      "Focus on form, control, and mindful movement over high-intensity training"
    ]
  },
  benefits: [
    {
      icon: "ri-heart-pulse-line",
      title: "Improved Strength & Stability",
      description: "Build sustainable core strength and full-body stability through controlled, precise movements that support long-term fitness."
    },
    {
      icon: "ri-shield-check-line",
      title: "Better Posture & Alignment",
      description: "Correct muscular imbalances and develop proper alignment patterns that carry into everyday movement and reduce chronic tension."
    },
    {
      icon: "ri-leaf-line",
      title: "Low-Impact, Joint-Friendly",
      description: "Reformer Pilates is gentle on joints while building strength. Perfect for all ages and fitness levels, including injury recovery."
    },
    {
      icon: "ri-shield-star-line",
      title: "Injury Prevention",
      description: "Strengthen stabilizing muscles and improve functional mobility to reduce injury risk in daily life and other physical activities."
    },
    {
      icon: "ri-brain-line",
      title: "Mind-Body Connection",
      description: "Focused breath work and mindful movement reduce stress, improve mental clarity, and regulate your nervous system."
    },
    {
      icon: "ri-time-line",
      title: "Sustainable Fitness",
      description: "This is fitness for the long game—not short-term intensity. Build strength and mobility that supports lifelong health and vitality."
    }
  ],
  studio: {
    description: "Our reformer studio is designed as a calm, premium environment where you can focus entirely on your practice. Natural light, minimal design, and carefully controlled capacity create the ideal space for mindful movement.",
    features: [
      {
        icon: "ri-group-line",
        title: "Small Classes",
        description: "Maximum 10 participants per class ensures individual attention and proper form guidance from expert instructors."
      },
      {
        icon: "ri-user-heart-line",
        title: "Expert Instructors",
        description: "Our team brings clinical backgrounds and years of experience in alignment-based movement and rehabilitation."
      },
      {
        icon: "ri-calendar-check-line",
        title: "Priority Booking",
        description: "Members enjoy priority access to classes and can build consistent relationships with their preferred instructors."
      }
    ]
  },
  classes: [
    {
      name: "Foundations",
      duration: "55 minutes",
      level: "All Levels",
      description: "Perfect for beginners or those returning to practice. Focus on fundamental reformer movements, breath work, and proper alignment. Build confidence and understanding of the equipment.",
      focus: [
        "Core reformer exercises and equipment familiarization",
        "Breath coordination and mindful movement",
        "Proper form and alignment principles",
        "Building strength and stability foundations"
      ]
    },
    {
      name: "Flow",
      duration: "55 minutes",
      level: "Intermediate",
      description: "For those comfortable with reformer basics. Fluid sequences that challenge strength, flexibility, and control. Emphasis on breath, precision, and sustainable movement patterns.",
      focus: [
        "Dynamic movement sequences with controlled transitions",
        "Progressive strength building and endurance",
        "Advanced breath work and core engagement",
        "Refinement of form and movement quality"
      ]
    },
    {
      name: "Restore",
      duration: "55 minutes",
      level: "All Levels",
      description: "Gentle, restorative practice focused on mobility, release, and nervous system regulation. Ideal for recovery days or as complement to more intense training.",
      focus: [
        "Gentle stretching and mobility work",
        "Fascial release and tension relief",
        "Breath-focused nervous system regulation",
        "Mindful movement for restoration and recovery"
      ]
    },
    {
      name: "Private Sessions",
      duration: "55 minutes",
      level: "Personalized",
      description: "One-on-one instruction tailored to your specific goals, body, and needs. Ideal for injury rehabilitation, specific goals, or those seeking completely personalized attention.",
      focus: [
        "Fully customized programming for individual goals",
        "Detailed form analysis and correction",
        "Injury rehabilitation and prevention strategies",
        "Accelerated progression with expert guidance"
      ]
    }
  ],
  instructors: {
    intro: "Our instructors bring clinical expertise, years of experience, and genuine care to every class. They prioritize your long-term wellbeing over short-term intensity.",
    team: [
      {
        name: "Sarah Mitchell",
        title: "Lead Instructor",
        bio: "15 years of experience in clinical Pilates and rehabilitation. Sarah specializes in alignment-based movement and works with clients recovering from injury.",
        image: "https://public.readdy.ai/ai/img_res/c0a503c9c34416e9b39ad75b522135d8.jpg"
      },
      {
        name: "James Chen",
        title: "Senior Instructor",
        bio: "Former physiotherapist with expertise in movement mechanics and breath work. James brings a clinical approach to sustainable strength building.",
        image: "https://readdy.ai/api/search-image?query=Professional%20pilates%20instructor%20man%20in%20his%2030s%20wearing%20grey%20activewear%20calm%20focused%20expression%20natural%20lighting%20wellness%20studio%20setting%20warm%20neutral%20tones%20editorial%20portrait%20photography%20style%20serene%20and%20confident%20aesthetic&width=600&height=800&seq=pilates003&orientation=portrait"
      },
      {
        name: "Emma Roberts",
        title: "Instructor",
        bio: "Specializes in restorative movement and nervous system regulation. Emma's classes focus on mindful practice and long-term wellbeing.",
        image: "https://readdy.ai/api/search-image?query=Professional%20pilates%20instructor%20woman%20in%20her%2030s%20wearing%20cream%20activewear%20peaceful%20expression%20soft%20natural%20lighting%20wellness%20studio%20environment%20warm%20beige%20tones%20editorial%20portrait%20photography%20style%20calm%20and%20approachable%20aesthetic&width=600&height=800&seq=pilates004&orientation=portrait"
      }
    ]
  },
  schedule: {
    intro: "Classes run throughout the week with morning, midday, and evening options. Members enjoy priority booking and can reserve their preferred times and instructors.",
    sample: [
      {
        day: "Monday",
        classes: [
          { time: "7:00am", name: "Foundations", instructor: "Sarah Mitchell" },
          { time: "9:30am", name: "Flow", instructor: "James Chen" },
          { time: "12:00pm", name: "Restore", instructor: "Emma Roberts" },
          { time: "6:00pm", name: "Flow", instructor: "Sarah Mitchell" }
        ]
      },
      {
        day: "Wednesday",
        classes: [
          { time: "7:00am", name: "Flow", instructor: "James Chen" },
          { time: "9:30am", name: "Foundations", instructor: "Emma Roberts" },
          { time: "12:00pm", name: "Flow", instructor: "Sarah Mitchell" },
          { time: "6:00pm", name: "Restore", instructor: "James Chen" }
        ]
      },
      {
        day: "Friday",
        classes: [
          { time: "7:00am", name: "Foundations", instructor: "Emma Roberts" },
          { time: "9:30am", name: "Flow", instructor: "Sarah Mitchell" },
          { time: "12:00pm", name: "Restore", instructor: "James Chen" },
          { time: "6:00pm", name: "Flow", instructor: "Emma Roberts" }
        ]
      },
      {
        day: "Saturday",
        classes: [
          { time: "8:00am", name: "Flow", instructor: "Sarah Mitchell" },
          { time: "10:00am", name: "Foundations", instructor: "James Chen" },
          { time: "12:00pm", name: "Restore", instructor: "Emma Roberts" }
        ]
      }
    ]
  },
  firstClass: [
    {
      title: "Arrive Early",
      description: "Come 10-15 minutes before your first class. Our team will greet you, show you the changing facilities, and help you get comfortable with the space."
    },
    {
      title: "Meet Your Instructor",
      description: "Your instructor will introduce themselves and ask about any injuries, concerns, or goals. This helps them provide appropriate modifications and guidance."
    },
    {
      title: "Equipment Introduction",
      description: "Before class begins, you'll receive a brief introduction to the reformer bed and how it works. Don't worry—it's simpler than it looks."
    },
    {
      title: "Focus on Form",
      description: "Your first class is about learning proper form and alignment. Move at your own pace, ask questions, and don't worry about keeping up with others."
    },
    {
      title: "Post-Class Support",
      description: "After class, your instructor is available for questions. We'll also help you book your next session and discuss membership options if interested."
    },
    {
      title: "Build Your Rhythm",
      description: "Consistency matters more than intensity. We recommend 2-3 classes per week to build sustainable strength and see long-term benefits."
    }
  ]
};
